import { Hammer, Paintbrush, Truck, CheckCircle2 } from "lucide-react";

/**
 * Build Queue / Lead Time badge shown on every configurator page.
 * Numbers mirror the workshop reality:
 *   • 3 working days for the carcass build
 *   • 5 working days for staining + curing
 *   • 5 active build slots open at any time
 *
 * Honest lead-time communication is one of the strongest conversion levers for
 * made-to-order furniture: it kills "how long am I waiting?" anxiety before it
 * starts. Tweak the constants here when ops capacity changes.
 */
const BUILD_DAYS = 3;
const FINISH_DAYS = 5;
const ACTIVE_SLOTS = 5;

const STAGES = [
  { icon: CheckCircle2, label: "Order placed", days: 0, accent: "text-[#E07A5F]" },
  { icon: Hammer, label: `${BUILD_DAYS}-day build`, days: BUILD_DAYS, accent: "text-amber-400" },
  { icon: Paintbrush, label: `${FINISH_DAYS}-day finish`, days: BUILD_DAYS + FINISH_DAYS, accent: "text-blue-400" },
  { icon: Truck, label: "Dispatch", days: BUILD_DAYS + FINISH_DAYS, accent: "text-green-400" },
];

const BuildQueueBadge = () => {
  const totalDays = BUILD_DAYS + FINISH_DAYS;
  return (
    <div
      className="border border-[#E07A5F]/25 bg-gradient-to-br from-[#E07A5F]/8 via-[#0A0A0A] to-[#0A0A0A] p-4 mb-6"
      data-testid="build-queue-badge"
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-[#F4F1DE]/50 mb-0.5">
            Live build queue
          </p>
          <p className="text-sm font-bold text-[#F4F1DE]">
            Dispatch in <span className="text-[#E07A5F]">~{totalDays} working days</span>
          </p>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <span className="text-[10px] uppercase tracking-widest text-[#F4F1DE]/50">Slots open</span>
          <span className="text-base font-bold text-[#F4F1DE]">
            <span className="text-amber-400">{ACTIVE_SLOTS}</span>
          </span>
        </div>
      </div>

      <div className="flex items-center gap-1 sm:gap-2">
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full bg-[#121212] border border-white/10 flex items-center justify-center ${s.accent}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-[9px] uppercase tracking-wide text-[#F4F1DE]/60 mt-1 whitespace-nowrap">
                  {s.label}
                </span>
              </div>
              {i < STAGES.length - 1 && (
                <div className="flex-1 h-px bg-gradient-to-r from-white/10 via-white/15 to-white/10 mx-1 mt-[-14px]" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BuildQueueBadge;
